import { Link } from 'wouter'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { SEOHead } from '@/components/seo-head'
import { Users, Target, Award, Globe, ArrowRight, CheckCircle } from 'lucide-react'

export function AboutUs() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "mainEntity": {
      "@type": "Organization",
      "name": "SourcedIn",
      "description": "Leading RFP marketplace connecting businesses worldwide through intelligent procurement solutions",
      "foundingDate": "2023",
      "founders": [
        {
          "@type": "Person",
          "name": "John Smith",
          "jobTitle": "CEO & Co-Founder"
        }
      ],
      "numberOfEmployees": "50-100",
      "location": {
        "@type": "Place",
        "addressLocality": "San Francisco",
        "addressRegion": "CA",
        "addressCountry": "US"
      }
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="About SourcedIn - Leading RFP Marketplace | Our Mission & Story"
        description="Learn about SourcedIn's mission to revolutionize B2B procurement through our intelligent RFP marketplace. Meet our team and discover how we're transforming business connections worldwide."
        keywords="about SourcedIn, RFP marketplace company, B2B procurement platform, business matching, vendor sourcing, procurement technology, company story"
        canonical="https://sourcedin.ai/about-us"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Revolutionizing B2B 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> Procurement</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            We're on a mission to make business connections more intelligent, transparent, 
            and successful through the world's most advanced RFP marketplace platform.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Mission</h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                To democratize access to business opportunities by creating the world's most 
                intelligent and transparent RFP marketplace, where every business - regardless 
                of size - can find their perfect partners and grow together.
              </p>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  <span className="text-gray-700">Eliminate procurement inefficiencies</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  <span className="text-gray-700">Create transparent business matching</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  <span className="text-gray-700">Empower businesses of all sizes</span>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl p-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                A world where every business opportunity finds its perfect match through 
                intelligent technology, creating a global ecosystem of successful partnerships 
                that drive innovation and economic growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Our Story</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Born from frustration with traditional procurement processes, 
              SourcedIn emerged to solve real business challenges.
            </p>
          </div>

          <div className="space-y-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">The Problem We Saw</h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Traditional RFP processes were broken. Buyers struggled to find qualified vendors, 
                  while suppliers missed opportunities buried in complex procurement systems. 
                  The process was slow, opaque, and inefficient for everyone involved.
                </p>
              </div>
              <div className="bg-red-50 p-8 rounded-xl">
                <div className="text-4xl font-bold text-red-600 mb-2">73%</div>
                <p className="text-gray-700">of businesses reported procurement inefficiencies before SourcedIn</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="bg-blue-50 p-8 rounded-xl md:order-1">
                <div className="text-4xl font-bold text-blue-600 mb-2">2023</div>
                <p className="text-gray-700">Founded with a vision to transform B2B procurement</p>
              </div>
              <div className="md:order-2">
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Solution</h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  We built an intelligent marketplace that uses AI to match buyers with the most 
                  qualified vendors, streamlines the entire RFP process, and provides transparency 
                  that benefits everyone in the ecosystem.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Our Core Values</h2>
            <p className="text-xl text-gray-600">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle className="text-xl">Transparency</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  We believe in open, honest communication and clear processes that benefit all parties.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-green-600" />
                </div>
                <CardTitle className="text-xl">Excellence</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  We strive for the highest quality in everything we do, from our platform to our service.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-purple-600" />
                </div>
                <CardTitle className="text-xl">Collaboration</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  We foster partnerships and teamwork, believing that together we achieve more.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="h-8 w-8 text-orange-600" />
                </div>
                <CardTitle className="text-xl">Innovation</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  We continuously evolve and embrace new technologies to solve complex challenges.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Impact & Stats */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Our Impact</h2>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              Since our launch, we've transformed how businesses connect and grow together
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">50K+</div>
              <div className="text-lg opacity-90">Active Users</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">$2B+</div>
              <div className="text-lg opacity-90">Business Value Created</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">150+</div>
              <div className="text-lg opacity-90">Countries Served</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">95%</div>
              <div className="text-lg opacity-90">Customer Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Meet Our Leadership</h2>
            <p className="text-xl text-gray-600">
              Experienced professionals passionate about transforming business connections
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center border-0 shadow-lg">
              <CardHeader>
                <div className="w-24 h-24 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold">
                  JS
                </div>
                <CardTitle className="text-2xl">John Smith</CardTitle>
                <CardDescription className="text-lg font-medium text-blue-600">CEO & Co-Founder</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Former procurement executive with 15+ years transforming enterprise buying processes at Fortune 500 companies.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg">
              <CardHeader>
                <div className="w-24 h-24 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold">
                  SC
                </div>
                <CardTitle className="text-2xl">Sarah Chen</CardTitle>
                <CardDescription className="text-lg font-medium text-purple-600">CTO & Co-Founder</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Tech visionary with deep expertise in AI and marketplace platforms, previously at Google and Amazon.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg">
              <CardHeader>
                <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold">
                  MJ
                </div>
                <CardTitle className="text-2xl">Michael Johnson</CardTitle>
                <CardDescription className="text-lg font-medium text-green-600">Head of Growth</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Growth strategist who scaled multiple B2B marketplaces from startup to IPO, expert in customer acquisition.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Join Our Mission
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Be part of the revolution transforming how businesses connect and grow together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/register">
              <Button size="lg" className="text-lg px-8 py-4">
                Get Started Today <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link href="/request-for-proposal-marketplace">
              <Button variant="outline" size="lg" className="text-lg px-8 py-4">
                Explore Opportunities
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
